import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Alert,
  Snackbar,
  Skeleton,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import dayjs from 'dayjs';
import Header from '../../../components/Header';
import PaymentStatusChip from '../../../components/PaymentStatusChip';
import { PedidoReembolsoCard } from '../../../components/financeiro';
import AlterarStatusReembolsoDialog from './AlterarStatusReembolsoDialog';
import AuditoriaReembolsoDialog from './AuditoriaReembolsoDialog';
import { useAuth } from '../../../context/AuthContext';
import { useProfile } from '../../../context/ProfileContext';
import { useLigaPermissions } from '../../../hooks/useLigaPermissions';
import {
  buscarPedidosReembolsoPorPagamento,
  executarReembolso as executarReembolsoService,
  finalizarReembolso as finalizarReembolsoService,
} from '../../../services/pedidoReembolsoService';
import { buscarSaldoContaLiga } from '../../../services/ligaService';
import { pagamentoPorId } from '../../../services/pagamentoInscricaoService';
import type { PedidoReembolso } from '../../../types/pedidoReembolso';
import type { PagamentoInscricao } from '../../../services/pagamentoInscricaoService';

const formatCurrency = (value?: number | null): string => {
  if (value == null) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const PedidosReembolsoPage: React.FC = () => {
  const { pagamentoId } = useParams<{ pagamentoId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const { currentProfile } = useProfile();
  const user = (auth?.user as any) || null;
  const isAdmin = user?.role === 'ADMIN';
  const isLigaProfile = currentProfile?.tipo === 'LIGA';
  const ligaId = isLigaProfile ? (currentProfile?.referenciaId ?? '') : '';

  const { canManagePayments, isGerente, isLoading: permissionsLoading } = useLigaPermissions(ligaId || null);
  const isOrganizador = isLigaProfile && (canManagePayments || isGerente);

  // Data state
  const [pedidos, setPedidos] = useState<PedidoReembolso[]>([]);
  const [pagamento, setPagamento] = useState<PagamentoInscricao | null>(null);
  const [saldo, setSaldo] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [alterarStatusDialog, setAlterarStatusDialog] = useState<{
    open: boolean;
    pedidoId: string;
    novoStatus: 'APROVADO' | 'RECUSADO';
  }>({ open: false, pedidoId: '', novoStatus: 'APROVADO' });

  const [auditoriaDialog, setAuditoriaDialog] = useState<{
    open: boolean;
    pedidoReembolsoId: string;
  }>({ open: false, pedidoReembolsoId: '' });

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async () => {
    if (!pagamentoId) return;
    setLoading(true);
    setError(null);
    try {
      const pedidosResult = await buscarPedidosReembolsoPorPagamento(pagamentoId);
      setPedidos(pedidosResult);

      // Get payment info from first pedido or load separately
      if (pedidosResult.length > 0 && pedidosResult[0].pagamentoInscricao) {
        setPagamento(pedidosResult[0].pagamentoInscricao as unknown as PagamentoInscricao);
      } else {
        const pag = await pagamentoPorId(pagamentoId);
        setPagamento(pag);
      }
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar pedidos de reembolso.');
    } finally {
      setLoading(false);
    }
  }, [pagamentoId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load saldo for organizer
  useEffect(() => {
    if (!isOrganizador || !ligaId) return;
    buscarSaldoContaLiga(ligaId)
      .then(setSaldo)
      .catch(() => setSaldo(null));
  }, [isOrganizador, ligaId]);

  // Execute validation logic
  const getExecuteValidation = (pedido: PedidoReembolso): { canExecute: boolean; reason: string | null } => {
    if (!pedido.invoiceId) {
      return { canExecute: false, reason: 'Este pagamento nao possui invoice. O reembolso deve ser feito manualmente.' };
    }
    if (pedido.meioPagamentoOriginal === 'BOLETO') {
      return { canExecute: false, reason: 'Pagamentos por boleto nao suportam reembolso automatico.' };
    }
    // Deadline check
    const dataPagamento = pagamento?.dataPagamento;
    if (dataPagamento) {
      const diasDesde = dayjs().diff(dayjs(dataPagamento), 'day');
      if (pedido.meioPagamentoOriginal === 'CARTAO' && diasDesde > 180) {
        return { canExecute: false, reason: 'O prazo para reembolso automatico expirou (180 dias para cartao).' };
      }
      if (pedido.meioPagamentoOriginal === 'PIX' && diasDesde > 90) {
        return { canExecute: false, reason: 'O prazo para reembolso automatico expirou (90 dias para PIX).' };
      }
    }
    // Balance check
    if (saldo != null && pedido.valorTotal != null && saldo < pedido.valorTotal) {
      return { canExecute: false, reason: 'O saldo da conta e inferior ao valor do reembolso.' };
    }
    return { canExecute: true, reason: null };
  };

  // Action handlers
  const handleExecutar = async (pedidoId: string) => {
    const confirmar = window.confirm('Tem certeza que deseja executar o reembolso automatico via gateway de pagamento?');
    if (!confirmar) return;
    try {
      await executarReembolsoService(pedidoId);
      setSnackbar({ open: true, message: 'Reembolso executado com sucesso.', severity: 'success' });
      await loadData();
    } catch (e: any) {
      const msg = e?.graphQLErrors?.[0]?.message || e?.message || 'Erro ao executar reembolso.';
      const isManualSuggestion = msg.includes('deve ser feito manualmente');
      setSnackbar({
        open: true,
        message: isManualSuggestion
          ? `${msg} Utilize a opcao "Finalizar Manualmente".`
          : msg,
        severity: 'error',
      });
    }
  };

  const handleFinalizar = async (pedidoId: string) => {
    const confirmar = window.confirm('Tem certeza que deseja finalizar manualmente este reembolso? Esta acao marca o pedido como concluido sem acionar o gateway de pagamento.');
    if (!confirmar) return;
    try {
      await finalizarReembolsoService(pedidoId);
      setSnackbar({ open: true, message: 'Reembolso finalizado manualmente com sucesso.', severity: 'success' });
      await loadData();
    } catch (e: any) {
      const msg = e?.graphQLErrors?.[0]?.message || e?.message || 'Erro ao finalizar reembolso.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  // Extract info for display
  const inscricoes = pagamento?.inscricoes || [];
  const atletaNomes = inscricoes.flatMap((i) =>
    (i as any).equipe?.atletas?.map((a: any) => a.usuario?.nome).filter(Boolean) || []
  );
  const categorias = inscricoes.map((i) => (i as any).categoriaTorneio?.nome).filter(Boolean);
  const torneioNome = (inscricoes[0] as any)?.categoriaTorneio?.torneio?.nome || '';
  const torneioLigaId = (inscricoes[0] as any)?.categoriaTorneio?.torneio?.ligaId || '';


  // Permission guard
  if (!loading && !permissionsLoading && !isOrganizador && !isAdmin) {
    // Athletes can still view their own refund requests
  }

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left" style={{ maxWidth: '1200px' }}>
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">
                    <Button
                      variant="text"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate(-1)}
                      sx={{ mb: 2 }}
                    >
                      Voltar
                    </Button>

                    <h3 className="fw-700 mb-3">Pedidos de Reembolso</h3>

                    {loading && (
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Skeleton variant="rectangular" height={120} />
                        <Skeleton variant="rectangular" height={200} />
                      </Box>
                    )}

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {!loading && !error && pagamento && (
                      <>
                        {/* Payment Summary */}
                        <Card variant="outlined" sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                              Resumo do Pagamento
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={2}>
                              <Box sx={{ minWidth: 120 }}>
                                <Typography variant="caption" color="text.secondary">Valor</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                  {formatCurrency(pagamento.valor as number)}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 120 }}>
                                <Typography variant="caption" color="text.secondary">Status</Typography>
                                <Box mt={0.5}>
                                  <PaymentStatusChip status={pagamento.status} />
                                </Box>
                              </Box>
                              <Box sx={{ minWidth: 120 }}>
                                <Typography variant="caption" color="text.secondary">Meio de Pagamento</Typography>
                                <Typography variant="body1">{pagamento.meioPagamento || '-'}</Typography>
                              </Box>
                              <Box sx={{ minWidth: 120 }}>
                                <Typography variant="caption" color="text.secondary">Data Pagamento</Typography>
                                <Typography variant="body1">
                                  {pagamento.dataPagamento ? dayjs(pagamento.dataPagamento).format('DD/MM/YYYY') : '-'}
                                </Typography>
                              </Box>
                              {torneioNome && (
                                <Box sx={{ minWidth: 120 }}>
                                  <Typography variant="caption" color="text.secondary">Torneio</Typography>
                                  <Typography variant="body1">{torneioNome}</Typography>
                                </Box>
                              )}
                              {categorias.length > 0 && (
                                <Box sx={{ minWidth: 120 }}>
                                  <Typography variant="caption" color="text.secondary">Categorias</Typography>
                                  <Typography variant="body1">{categorias.join(', ')}</Typography>
                                </Box>
                              )}
                              {atletaNomes.length > 0 && (
                                <Box sx={{ minWidth: 200 }}>
                                  <Typography variant="caption" color="text.secondary">Atletas</Typography>
                                  <Typography variant="body1">{atletaNomes.join(', ')}</Typography>
                                </Box>
                              )}
                            </Box>
                          </CardContent>
                        </Card>

                        {/* Refund Requests */}
                        {pedidos.length === 0 ? (
                          <Alert severity="info">Nenhum pedido de reembolso encontrado para este pagamento.</Alert>
                        ) : (
                          pedidos.map((pedido) => {
                            const { canExecute, reason } = getExecuteValidation(pedido);
                            return (
                              <PedidoReembolsoCard
                                key={pedido.id}
                                pedido={pedido}
                                isOrganizador={isOrganizador}
                                isAdmin={isAdmin}
                                canExecute={canExecute}
                                executeDisabledReason={reason}
                                onAprovar={() => setAlterarStatusDialog({ open: true, pedidoId: pedido.id, novoStatus: 'APROVADO' })}
                                onRecusar={() => setAlterarStatusDialog({ open: true, pedidoId: pedido.id, novoStatus: 'RECUSADO' })}
                                onExecutar={() => handleExecutar(pedido.id)}
                                onFinalizar={() => handleFinalizar(pedido.id)}
                                onHistorico={() => setAuditoriaDialog({ open: true, pedidoReembolsoId: pedido.id })}
                              />
                            );
                          })
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <AlterarStatusReembolsoDialog
        open={alterarStatusDialog.open}
        pedidoId={alterarStatusDialog.pedidoId}
        novoStatus={alterarStatusDialog.novoStatus}
        onClose={() => setAlterarStatusDialog((prev) => ({ ...prev, open: false }))}
        onSuccess={loadData}
      />

      <AuditoriaReembolsoDialog
        open={auditoriaDialog.open}
        pedidoReembolsoId={auditoriaDialog.pedidoReembolsoId}
        onClose={() => setAuditoriaDialog((prev) => ({ ...prev, open: false }))}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PedidosReembolsoPage;
